import dayjs from "lib/dayjs";

function safeJson(value, fallback = []) {
  if (value == null) return fallback;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function numberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function refundingMaturityKey(dealId, rowIndex) {
  return `${dealId}:${rowIndex}`;
}

export function getRefundingCandidateMaturities({
  deal,
  settlementDate,
  maxDaysBeforeCall = 90,
}) {
  const settlement = dayjs(settlementDate).startOf("day");
  if (!settlement.isValid()) return [];

  const rows = safeJson(deal?.structure, []);
  if (!Array.isArray(rows)) return [];

  const windowDays = Math.max(0, Number(maxDaysBeforeCall) || 0);

  return rows.map((row, rowIndex) => {
    const principal = numberOrZero(row?.principal);
    const maturityDate = row?.date || row?.maturity_date || row?.maturityDate || "";
    const callDate = row?.call_date || row?.callDate || "";
    const maturity = dayjs(maturityDate).startOf("day");
    const call = dayjs(callDate).startOf("day");
    const callPriceRaw = Number(row?.call_price ?? row?.callPrice ?? 100);
    const callPrice = Number.isFinite(callPriceRaw) && callPriceRaw > 0
      ? callPriceRaw
      : 100;

    let ineligibleReason = "";
    if (!(principal > 0)) ineligibleReason = "No outstanding principal";
    else if (!maturity.isValid() || !maturity.isAfter(settlement, "day"))
      ineligibleReason = "Matured by settlement";
    else if (!call.isValid()) ineligibleReason = "No call date";
    else if (call.isAfter(maturity, "day")) ineligibleReason = "Call date is after maturity";
    else if (settlement.isBefore(call.subtract(windowDays, "day"), "day"))
      ineligibleReason = `Call date is more than ${windowDays} days after settlement`;

    return {
      key: refundingMaturityKey(deal?.id, rowIndex),
      rowIndex,
      maturityDate,
      callDate,
      callPrice,
      principal,
      coupon: numberOrZero(row?.coupon),
      depositAmount: principal * callPrice / 100,
      eligible: !ineligibleReason,
      ineligibleReason,
    };
  });
}

export function buildRefundedIssueSnapshots({
  deals,
  selectedKeys,
  settlementDate,
  maxDaysBeforeCall = 90,
}) {
  const selected = selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys || []);

  return (deals || []).flatMap((deal) => {
    const maturities = getRefundingCandidateMaturities({
      deal,
      settlementDate,
      maxDaysBeforeCall,
    }).filter((m) => selected.has(m.key));

    if (!maturities.length) return [];

    return [{
      entry_type: "ds_issue",
      ds_deal_id: deal.id,
      issue_name: deal.name || deal.deal_name || "(Untitled Issue)",
      settlement_date: settlementDate,
      eligibility_window_days: maxDaysBeforeCall,
      coupon_frequency: deal.coupon_frequency || "semiannual",
      day_count: deal.day_count || "30/360",
      deposit_amount: maturities.reduce((sum, m) => sum + m.depositAmount, 0),
      selected_maturities: maturities.map((m) => ({
        source_row_index: m.rowIndex,
        maturity_date: m.maturityDate,
        call_date: m.callDate,
        call_price: m.callPrice,
        principal: m.principal,
        coupon: m.coupon,
        deposit_amount: m.depositAmount,
      })),
    }];
  });
}

export function selectedKeysFromRefundedIssues(refundedIssues) {
  const entries = safeJson(refundedIssues, []);
  if (!Array.isArray(entries)) return [];
  return entries.flatMap((entry) => {
    if (entry?.entry_type !== "ds_issue" || !entry?.ds_deal_id) return [];
    return (entry.selected_maturities || []).map((m) =>
      refundingMaturityKey(entry.ds_deal_id, m.source_row_index)
    );
  });
}
